import { BOEntityDefinition, PanelSourceMetadata, ImisResponse, PayloadFieldToTypeMapping } from "../models/imis.models";
import { DocumentsUtility } from "./documents.utility";
import { PayloadUtility } from "./payload.utility";
import { RestUtility } from "./rest.utility";

export class DataSourcesUtility {

    public static async getAllDataSources(onlyPanelSources: boolean, isSystem: boolean): Promise<BOEntityDefinition[]> {
        let boEntityDefinitionItems: BOEntityDefinition[] = [];

        if (onlyPanelSources) {
            let response = await RestUtility.restApiGetDataAll<BOEntityDefinition>("/api/boentitydefinition");
            boEntityDefinitionItems = response.Items;
        } else {
            let businessObjectDefinitionsResponse = await DocumentsUtility.getDocumentsByType("BOD", isSystem, true);
            let businessObjectDefinitionsNames = businessObjectDefinitionsResponse.Items.map(i => i.Name);
            
            let promises = businessObjectDefinitionsNames.map(bodName => this.getDataSourceDefinition(bodName));
            boEntityDefinitionItems = await Promise.all(promises);
        }

        return boEntityDefinitionItems; 
    }

    public static async getDataSourceDefinition(dataSourceName: string, rawResponse?: boolean): Promise<BOEntityDefinition> {
        let boEntityDefinition: BOEntityDefinition;

        try {
            boEntityDefinition = await RestUtility.restApiGetData<BOEntityDefinition>(`/api/boentitydefinition/${dataSourceName}`, null, rawResponse);
        } catch (error) {
            boEntityDefinition = null;
        }

        return boEntityDefinition;
    }

    public static async getDataSourceMetadata(dataSourceName: string): Promise<PanelSourceMetadata> {
        let panelSourceItem: PanelSourceMetadata;

        try {
            panelSourceItem = await RestUtility.restApiGetData<PanelSourceMetadata>(`/api/metadata/${dataSourceName}`);
        } catch (error) {
            panelSourceItem = null;
        }

        return panelSourceItem;
    }

    public static async getDataSourceRecord<T = ImisResponse>(tableName: string, identity: any[], rawResponse?: boolean): Promise<T> {
        let url = this.getRequestUrl(tableName, identity);

        return RestUtility.restApiGetData(url, null, rawResponse);
    }

    /**
     * 
     * @param tableName data source name
     * @param payload key-value pairs of mappings between field names and values of exactly the data type expected by the datasource (i.e. integers cannot be strings)
     * @param fieldToTypeMapping (optional) if the field does not have a standard mapping to a literal data type then the field can be specified here
     * @param rawResponse dont parse the payload and return it as iMIS sends it (great for when you need to post the changes back) 
     * @returns the imis response 
     */
    public static async addDataSourceRecord(tableName: string, payload: any, fieldToTypeMapping?: PayloadFieldToTypeMapping, rawResponse?: boolean): Promise<any> {
        let url: string = "/api/" + tableName;
        let mappedJsonPayload = PayloadUtility.getPanelSourcePayload(tableName, null, payload, null, fieldToTypeMapping);

        return RestUtility.restApiSendData(url, mappedJsonPayload, "POST", rawResponse);
    }

    /**
     * 
     * @param tableName data source name
     * @param identity an array of values that identify the unique identifiers for the datasource (possible combinations: [id, ordinal] or [ordinal])
     * @param payload key-value pairs of mappings between field names and values of exactly the data type expected by the datasource (i.e. integers cannot be strings)
     * @param alternativeIdentityFieldNames (optional) if the field names are not the default `ID` as the primary and `Ordinal` as the secondary then this is where you would specify the alternative names
     * @param fieldToTypeMapping (optional) if the field does not have a standard mapping to a literal data type then the field can be specified here
     * @param rawResponse dont parse the payload and return it as iMIS sends it (great for when you need to post the changes back)
     * @returns the imis response
     */
    public static async updateDataSourceRecord(tableName: string, identity: any[], payload: any, alternativeIdentityFieldNames?: string[], fieldToTypeMapping?: PayloadFieldToTypeMapping, rawResponse?: boolean): Promise<any> {
        let url = this.getRequestUrl(tableName, identity);
        let mappedJsonPayload = PayloadUtility.getPanelSourcePayload(tableName, identity, payload, alternativeIdentityFieldNames, fieldToTypeMapping);

        return RestUtility.restApiSendData(url, mappedJsonPayload, "PUT", rawResponse);
    }

    public static async deleteDataSourceRecord(tableName: string, identity: any[]): Promise<boolean> {
        let url = this.getRequestUrl(tableName, identity);

        return RestUtility.restApiDeleteData(url);
    }

    public static async duplicateDataSourceDefinition(targetSourceName: string, newSourceName: string): Promise<BOEntityDefinition> {
        let newDataSource = await this.getDataSourceDefinition(targetSourceName, true);
        newDataSource.EntityTypeName = newSourceName;
        newDataSource.Description = `Duplicate datasource of ${targetSourceName}`;

        return RestUtility.restApiSendData("/api/boentitydefinition", newDataSource, "POST", true);
    }

    private static getRequestUrl(tableName: string, identity: any[]): string {
        return "/api/" + tableName + "/~" + identity.join("|");
    }
}